/**
 * What the application shell knows, shared with every screen inside it.
 *
 * The visible apps (after permissions and optional modules), the app the
 * current URL belongs to, the person's pinned and recent pages, and the
 * handles that open the launcher, the command center and quick create from
 * anywhere — a keyboard shortcut, the mobile tab bar, an empty state.
 *
 * Outside the shell (sign-in, the vendor portal, the public forms) there is
 * no provider and `useShell()` returns null, so shared components such as
 * PageHeader can consult it without caring where they are rendered.
 */
import { createContext, useContext, useMemo } from 'react';
import { useTenant } from '../context/TenantContext.jsx';
import { APPS } from './apps.js';
import { CREATE_ACTIONS } from './createActions.js';

const ShellContext = createContext(null);

export const ShellProvider = ShellContext.Provider;

/** The shell's context, or null outside the application shell. */
export function useShell() {
  return useContext(ShellContext);
}

const pathOf = (to) => String(to).split('?')[0];

/**
 * The apps this person can use in this organization, each carrying only the
 * sections and pages they may open.
 *
 * The rules are the sidebar's, unchanged: an item is visible when it names no
 * permission or the caller holds any one of them; an optional module's app
 * disappears while the module is switched off — but only once the module list
 * has actually answered, so "not loaded yet" never reads as "switched off".
 * While permissions are loading, permission-gated items are held back rather
 * than flashed and withdrawn.
 */
export function useVisibleApps() {
  const { can, loading, modulesKnown, isModuleEnabled } = useTenant();

  return useMemo(() => {
    const moduleOff = (app) => Boolean(app.module) && modulesKnown && !isModuleEnabled(app.module);
    const visible = (item) => !item.permissions || (!loading && can(...item.permissions));

    return APPS.filter((app) => !moduleOff(app))
      .map((app) => {
        const sections = app.sections
          .map((section) => ({ ...section, items: section.items.filter(visible) }))
          .filter((section) => section.items.length > 0);
        const items = sections.flatMap((section) => section.items);

        // Where opening the app lands: its home when that is a page the caller
        // may open (a directory home always is), else its first visible page.
        const homeItem = items.find((item) => pathOf(item.to) === app.home);
        const isDirectory = !app.sections.some((section) => section.items.some((item) => pathOf(item.to) === app.home));
        const nativeItems = items.filter((item) => !item.shared);
        const homeTo = homeItem || isDirectory ? app.home : (nativeItems[0]?.to ?? items[0]?.to);

        return { ...app, sections, items, homeTo, isDirectory };
      })
      .filter((app) => app.items.some((item) => !item.shared));
  }, [can, loading, modulesKnown, isModuleEnabled]);
}

/** The create actions this person may take, grouped under their app. */
export function useCreateActions(visibleApps) {
  const { can, loading } = useTenant();

  return useMemo(() => {
    if (loading) return [];
    const appKeys = new Set(visibleApps.map((app) => app.key));
    return CREATE_ACTIONS.filter((action) => appKeys.has(action.app) && can(...action.permissions));
  }, [can, loading, visibleApps]);
}
